/**
 * Data Entry > Status Message — the two rules the grid and the side panel both need:
 * which shipment types a message applies to, and whether the user may change it.
 *
 * Keeping them here (rather than inline in the grid) is what stops the panel and the grid
 * from drifting into two different answers for the same row.
 */

/**
 * One entry per shipment type tab, in the order the operator sees them.
 *
 * `matches` mirrors StatusRepository.search_status_messages (status_flow/repository.py):
 * a message shows up on a tab when it has an il_status_message_type row whose transport_type
 * is one this tab reads AND whose shipment_type is either this tab's direction or NULL
 * ("both directions"). Trucking has no direction, so only NULL rows reach it.
 *
 * If this drifts from the server rule the column lies — it would claim a message applies to a
 * type where the operator can't actually pick it, which is the exact confusion the column was
 * added to remove. The abbreviations are the ones used for OPUS reference numbers
 * (shipment/constants.py TAB_OPUS_PREFIX), so operators already read them as shipment types.
 */
export const SHIPMENT_TYPE_TABS = [
    { code: 'OIM', direction: true, transports: ['All', 'Ocean'] },
    { code: 'OEX', direction: false, transports: ['All', 'Ocean'] },
    { code: 'AIM', direction: true, transports: ['All', 'Air'] },
    { code: 'AEX', direction: false, transports: ['All', 'Air'] },
    { code: 'OTH', direction: null, transports: ['All', 'Trucking'] },
];

/** Shipment type abbreviations a message applies to, e.g. ['OIM', 'OEX', 'OTH']. */
export const shipmentTypeCodes = (types) => {
    const rows = types ?? [];
    return SHIPMENT_TYPE_TABS.filter(({ direction, transports }) =>
        rows.some(
            (row) =>
                transports.includes(row?.transport_type) &&
                // `== null` covers both null and undefined: a row with no direction applies to both.
                (row?.shipment_type === direction || row?.shipment_type == null),
        ),
    ).map(({ code }) => code);
};

/** The role that owns the status message catalogue and is not bound by the guards below. */
export const ADMINISTRATOR_ROLE = 'Administrator';
export const isAdministrator = (userRole) => (userRole ?? []).includes(ADMINISTRATOR_ROLE);

/**
 * Whether the side panel opens read-only (Save disabled).
 *
 * The criterion is "the server would reject a structural edit", so the panel never offers a
 * Save that comes back as an error:
 *  - `is_default` — system-provided wording. The catalog is seeded, not user-authored.
 *  - `protected`  — content_display is itself a code trigger (date sync, todo rules, autofill);
 *                   StatusFlowService.update_status_message rejects any structural change to it.
 *
 * An Administrator is past both — the server skips those guards for is_administrator — so
 * locking the panel would hide an edit the server would have accepted.
 *
 * Read-only is about the message's wording, input fields and shipment types — the things this
 * panel edits. Active and Warning stay switchable from the grid on every row, which is how a
 * default message is taken out of use.
 */
export const isReadOnlyStatusMessage = (row, userRole) =>
    !isAdministrator(userRole) && Boolean(row?.is_default || row?.protected);

/**
 * Why this message can't be deleted, or null when it can. Wording is what the operator sees in
 * the snackbar, so each line says the cause and the way forward.
 *
 * Checked in the same order as StatusFlowService.delete_status_message so the client and the
 * server never disagree about which reason applies. The client check is a courtesy — it answers
 * instantly and without a round trip — and the server stays the authority, because in_use is a
 * snapshot from page load and a colleague may have used the message since.
 */
export const deleteBlockReason = (row, userRole) => {
    if (!isAdministrator(userRole)) {
        if (row?.protected) {
            return 'This is a system status message that other automation depends on. It cannot be deleted.';
        }
        if (row?.is_default) {
            return 'This is a default status message and cannot be deleted. Turn off Active to stop using it.';
        }
    }
    if (row?.in_use) {
        return 'This status message is already recorded on shipments, so deleting it would remove those records. Turn off Active to stop using it.';
    }
    if (row?.has_todo_rules) {
        return 'A to-do rule uses this status message. Remove the rule first, or turn off Active to stop using it.';
    }
    return null;
};
